<template>
  <section class="location-finder" aria-labelledby="location-finder-title">
    <div class="location-finder__panel">
      <h1 id="location-finder-title" class="sr-only">Find a location</h1>

      <form
        class="location-search"
        role="search"
        @submit.prevent="searchLocations"
      >
        <label for="location-search">Search by zip code</label>
        <div class="location-search__field">
          <input
            id="location-search"
            v-model.trim="query"
            type="search"
            inputmode="numeric"
            autocomplete="postal-code"
            maxlength="5"
            placeholder="Enter a zip code"
          >
          <button type="submit" aria-label="Search locations">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="10.8" cy="10.8" r="6.7" />
              <path d="m16 16 5 5" />
            </svg>
          </button>
        </div>
      </form>

      <p v-if="statusMessage" class="location-finder__status" role="status">
        {{ statusMessage }}
      </p>

      <div ref="resultsEl" class="location-results" aria-live="polite">
        <button
          v-for="location in filteredLocations"
          :key="location.id"
          type="button"
          class="location-card"
          :data-location-id="location.id"
          :class="{ 'location-card--active': location.id === activeLocationId }"
          :aria-pressed="location.id === activeLocationId"
          @click="selectLocation(location)"
        >
          <strong class="location-card__name">{{ location.name }}</strong>
          <span><b>Date:</b> {{ location.date }}</span>
          <span><b>Hours:</b> {{ location.hours }}</span>
          <span><b>Address:</b> {{ location.address }}</span>
          <span
            >{{ location.city }}, {{ location.state }} {{ location.zip }}</span
          >
          <span class="location-card__organization">{{
            location.organization
          }}</span>
        </button>
      </div>
    </div>

    <div class="location-finder__map-wrap">
      <div
        ref="mapEl"
        class="location-finder__map"
        aria-label="Map of locations"
      />
      <div v-if="!mapboxToken" class="location-finder__map-message">
        <div>
          <strong>Mapbox token needed</strong>
          <span>Add NUXT_PUBLIC_MAPBOX_ACCESS_TOKEN to your .env file.</span>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import mapboxgl from 'mapbox-gl';
import type { Map, Marker } from 'mapbox-gl';

type Location = {
  id: number;
  name: string;
  date: string;
  hours: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  organization: string;
  coordinates: [number, number];
};

const locations: Location[] = [
  {
    id: 1,
    name: 'El Cajon Library',
    date: 'October 3',
    hours: '1:30–2:30pm',
    address: '201 E Douglas Ave',
    city: 'El Cajon',
    state: 'CA',
    zip: '92020',
    organization: 'San Diego County Library',
    coordinates: [-116.96074, 32.79305]
  },
  {
    id: 2,
    name: 'Ramona Library',
    date: 'Tuesday, October 6',
    hours: '4pm',
    address: '1275 Main St',
    city: 'Ramona',
    state: 'CA',
    zip: '92065',
    organization: 'San Diego County Library',
    coordinates: [-116.87321, 33.039906]
  },
  {
    id: 3,
    name: 'The Ryan Family YMCA',
    date: 'Work in Progress',
    hours: 'Work in Progress',
    address: '4390 Valeta Street',
    city: 'San Diego',
    state: 'CA',
    zip: '92107',
    organization: 'YMCA of San Diego County',
    coordinates: [-117.2328944, 32.7493275]
  },
  {
    id: 4,
    name: 'Jackie Robinson Family YMCA',
    date: 'Work in Progress',
    hours: 'Work in Progress',
    address: '151 YMCA Way',
    city: 'San Diego',
    state: 'CA',
    zip: '92102',
    organization: 'YMCA of San Diego County',
    coordinates: [-117.097999, 32.705016]
  },
  {
    id: 5,
    name: 'Copley-Price Family YMCA',
    date: 'Monday,October 5',
    hours: '4pm',
    address: '4300 El Cajon Blvd',
    city: 'San Diego',
    state: 'CA',
    zip: '92105',
    organization: 'YMCA of San Diego County',
    coordinates: [-117.1011, 32.75537]
  }
];

const config = useRuntimeConfig();
const mapboxToken = config.public.mapboxAccessToken as string;
const mapEl = ref<HTMLElement | null>(null);
const resultsEl = ref<HTMLElement | null>(null);
const query = ref('92020');
const appliedQuery = ref('');
const activeLocationId = ref<number | null>(1);
const statusMessage = ref('');
let map: Map | null = null;
let markers: Array<{ id: number; marker: Marker; element: HTMLElement }> = [];

const filteredLocations = computed(() => {
  const term = appliedQuery.value.toLowerCase();
  if (!term) return locations;
  return locations.filter((location) =>
    [location.zip, location.name, location.address, location.city].some(
      (value) => value.toLowerCase().includes(term)
    )
  );
});

function searchLocations() {
  appliedQuery.value = query.value;
  const count = filteredLocations.value.length;
  statusMessage.value = count
    ? `${count} location${count === 1 ? '' : 's'} found near ${query.value}.`
    : `No locations found near ${query.value}. Try another zip code.`;

  nextTick(() => {
    updateMarkers();
    fitVisibleLocations();
    resultsEl.value?.scrollTo({ top: 0, behavior: 'smooth' });
  });
}

function createMarkerElement(location: Location) {
  const element = document.createElement('button');
  element.type = 'button';
  element.className = 'map-marker';
  element.setAttribute('aria-label', `View ${location.name}`);
  element.setAttribute(
    'aria-pressed',
    String(location.id === activeLocationId.value)
  );
  element.innerHTML = '<img src="/pin.png" alt="" width="52" height="72">';
  element.addEventListener('click', () => selectLocation(location, true));
  return element;
}

function selectLocation(location: Location, scrollToResult = false) {
  activeLocationId.value = location.id;
  markers.forEach(({ id, element }) => {
    const isActive = id === location.id;
    element.classList.toggle('map-marker--active', isActive);
    element.setAttribute('aria-pressed', String(isActive));
  });
  map?.flyTo({ center: location.coordinates, zoom: 13.5, duration: 900 });

  if (scrollToResult) {
    nextTick(() => {
      const results = resultsEl.value;
      const card = results?.querySelector<HTMLElement>(
        `[data-location-id="${location.id}"]`
      );
      if (!results || !card) return;

      const top =
        results.scrollTop +
        card.getBoundingClientRect().top -
        results.getBoundingClientRect().top;
      results.scrollTo({ top, behavior: 'smooth' });
    });
  }
}

function updateMarkers() {
  const visibleIds = new Set(filteredLocations.value.map(({ id }) => id));
  markers.forEach(({ id, element }) => {
    element.hidden = !visibleIds.has(id);
  });
}

function fitVisibleLocations() {
  if (!map || !filteredLocations.value.length) return;
  const bounds = new mapboxgl.LngLatBounds();
  filteredLocations.value.forEach(({ coordinates }) =>
    bounds.extend(coordinates)
  );
  map.fitBounds(bounds, { padding: 70, maxZoom: 13, duration: 900 });
}

onMounted(() => {
  if (!mapEl.value || !mapboxToken) return;

  mapboxgl.accessToken = mapboxToken;
  map = new mapboxgl.Map({
    container: mapEl.value,
    style: 'mapbox://styles/mapbox/streets-v12',
    center: [-117.126, 32.742],
    zoom: 11.4,
    attributionControl: false
  });
  map.addControl(
    new mapboxgl.NavigationControl({ showCompass: false }),
    'top-right'
  );
  map.addControl(new mapboxgl.AttributionControl({ compact: true }));

  markers = locations.map((location) => {
    const element = createMarkerElement(location);
    if (location.id === activeLocationId.value)
      element.classList.add('map-marker--active');
    const marker = new mapboxgl.Marker({ element, anchor: 'bottom' })
      .setLngLat(location.coordinates)
      .addTo(map as Map);
    return { id: location.id, marker, element };
  });

  map.once('load', fitVisibleLocations);
});

onBeforeUnmount(() => {
  markers.forEach(({ marker }) => marker.remove());
  map?.remove();
});
</script>

<style lang="scss">
.location-finder {
  --finder-yellow: #ffc91f;
  --finder-teal: #087f78;
  --finder-purple: #b451b7;

  display: grid;
  grid-template-columns: minmax(310px, 370px) minmax(0, 1fr);
  gap: 16px;
  width: 100%;
  min-height: 720px;
  height: calc(100vh - 8px);
  padding: 24px;
  background: var(--finder-yellow);
  color: #111;

  &__panel {
    display: flex;
    min-height: 0;
    flex-direction: column;
  }

  &__status {
    margin: 8px 0 0;
    padding: 8px 12px;
    background: #fff4c5;
    font-size: 14px;
  }

  &__map-wrap {
    position: relative;
    min-height: 500px;
    overflow: hidden;
    border: 2px solid #fff;
    background: #dce9e7;
  }

  &__map {
    width: 100%;
    height: 100%;
  }

  &__map-message {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    padding: 24px;
    background:
      radial-gradient(circle at 60% 40%, #edf5ee 0 8%, transparent 9%),
      linear-gradient(135deg, #d9eadf, #b7d4e5);
    text-align: center;

    div {
      display: grid;
      gap: 8px;
      max-width: 420px;
      padding: 22px;
      background: rgba(255, 255, 255, 92%);
      box-shadow: 0 10px 30px rgba(0, 0, 0, 12%);
    }
  }
}

.location-search {
  label {
    display: block;
    margin-bottom: 7px;
    font-size: 14px;
    font-weight: 700;
  }

  &__field {
    position: relative;
    border-bottom: 5px solid var(--finder-purple);
  }

  input {
    width: 100%;
    height: 48px;
    padding: 0 52px 0 22px;
    border: 0;
    border-radius: 0;
    background: #fff;
    color: #111;
    outline: 0;

    &:focus-visible {
      box-shadow: inset 0 0 0 3px #111;
    }
  }

  button {
    position: absolute;
    top: 0;
    right: 0;
    display: grid;
    width: 52px;
    height: 48px;
    place-items: center;

    svg {
      width: 23px;
      fill: none;
      stroke: currentcolor;
      stroke-linecap: round;
      stroke-width: 2.5;
    }
  }
}

.location-results {
  display: grid;
  flex: 1;
  gap: 7px;
  min-height: 0;
  margin-top: 7px;
  padding-right: 9px;
  overflow-y: auto;
  scrollbar-color: var(--finder-purple) #f9e5f9;
  scrollbar-width: auto;

  &::-webkit-scrollbar {
    width: 16px;
  }

  &::-webkit-scrollbar-track {
    background: #f9e5f9;
    border-radius: 20px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--finder-purple);
    border: 4px solid #f9e5f9;
    border-radius: 20px;
  }
}

.location-card {
  display: flex;
  width: 100%;
  height: 250px;
  flex-direction: column;
  align-items: flex-start;
  padding: 15px 23px 18px;
  border: 4px solid transparent;
  background: #fff;
  color: #111;
  line-height: 1.45;
  text-align: left;
  transition:
    border-color 160ms ease,
    transform 160ms ease;

  &:hover,
  &:focus-visible,
  &--active {
    border-color: var(--finder-purple);
    outline: 0;
  }

  &:hover {
    transform: translateX(2px);
  }

  &__name {
    margin-bottom: 6px;
    font-size: 17px;
  }

  &__organization {
    margin-top: auto;
    padding: 5px 9px;
    background: var(--finder-teal);
    color: #fff;
  }
}

:global(.map-marker) {
  width: 52px;
  height: 72px;
  background: transparent;
}

:global(.map-marker img) {
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 2px 1px rgba(0, 0, 0, 20%));
  transition: filter 160ms ease, transform 160ms ease;
}

:global(.map-marker--active) {
  z-index: 2;
}

:global(.map-marker--active img) {
  filter: hue-rotate(122deg) drop-shadow(0 2px 1px rgba(0, 0, 0, 20%));
  transform: scale(1.08);
}

:global(.map-marker:hover),
:global(.map-marker:focus-visible) {
  z-index: 2;
  outline: 0;
}

:global(.map-marker:hover img),
:global(.map-marker:focus-visible img) {
  filter: drop-shadow(0 0 3px #fff) drop-shadow(0 2px 1px rgba(0, 0, 0, 20%));
  transform: translateY(-3px);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (width <= 767px) {
  .location-finder {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto minmax(360px, 48vh) minmax(0, 1fr);
    gap: 8px;
    height: 100vh;
    height: 100dvh;
    min-height: 680px;
    padding: 16px 8px 8px;
    overflow: hidden;

    &__panel {
      display: contents;
    }

    &__map-wrap {
      grid-row: 3;
      min-height: 0;
    }

    &__status {
      grid-row: 2;
      margin-top: 0;
    }
  }

  .location-search {
    grid-row: 1;
  }

  .location-results {
    grid-row: 4;
    gap: 8px;
    margin-top: 0;
    padding-right: 8px;
  }

  .location-card {
    min-height: 205px;
    padding: 13px 19px 17px;
  }
}
</style>
